import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Stack, Alert, CircularProgress } from '@mui/material';
import { ArrowLeft } from '@phosphor-icons/react';
import { useBusiness } from '../../context/BusinessContext';
import { useBusinessSubscription } from '../../hooks/useBusinessSubscription';
import StaffShiftCalendar from '../StaffManagement/StaffShiftCalendar';
import api from '../../utils/api';
import { useState, useEffect } from 'react';
import useThemeStyles from '../../theme/useThemeStyles';

function StaffSchedulePage() {
  const styles = useThemeStyles();
  const { staffMemberId } = useParams();
  const navigate = useNavigate();
  const { selectedBusiness, businessId } = useBusiness();
  const { planKey, loading: subscriptionLoading } = useBusinessSubscription();
  const isFreePlan = subscriptionLoading || planKey === 'supplier_free';
  const [staffMember, setStaffMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (staffMemberId) {
      loadStaffMember();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [staffMemberId]);

  const loadStaffMember = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/api/supplier/staff-members/${staffMemberId}`);
      setStaffMember(response.data.staffMember);
    } catch (err) {
      setError('Unable to load staff member details');
    } finally {
      setLoading(false);
    }
  };

  const getStaffMemberName = () => {
    if (staffMember?.user) {
      const user = staffMember.user;
      return `${user.firstName || ''} ${user.lastName || ''}`.trim() || staffMember.name;
    }
    return staffMember?.name || 'Staff Member';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !staffMember) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          variant="text"
          startIcon={<ArrowLeft size={20} />}
          onClick={() => navigate('/supplier/schedules')}
          sx={{ mb: 2 }}
        >
          Back to Schedules
        </Button>
        <Alert severity="error">
          {error || 'Staff member not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        variant="text"
        startIcon={<ArrowLeft size={20} />}
        onClick={() => navigate('/supplier/schedules')}
        sx={{ mb: 3 }}
      >
        Back to Schedules
      </Button>

      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          {getStaffMemberName()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage shifts and availability. Click and drag on the calendar to add shifts, or click existing shifts to edit them.
        </Typography>
      </Stack>

      <StaffShiftCalendar
        teamMemberId={staffMemberId}
        businessId={businessId || selectedBusiness?.business?._id}
        isFreePlan={isFreePlan}
      />
    </Box>
  );
}

export default StaffSchedulePage;

